import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { translateOptional } from "@/i18n/translate";
import { cn } from "@/lib/utils";
import { Ago, Meter, SeverityIcon, Sparkline } from "../../components/primitives";
import { DASH, formatCount, formatDuration, formatMb, formatMs, formatStamp } from "../../format";
import type { PodError, PodMetrics, PodModel } from "../../types";
import { FactList, StatTile, SubHeading } from "../backend/stat-tile";
import { isRecent, LIMITS, toneText, type Tone } from "../backend/tone";
import {
  gpuHistory,
  INFERENCE_ROUTES,
  loopMax5m,
  OTHER_ROUTE,
  pcieLink,
  runsOnCpu,
  runsOnGpu,
  SLOWING_THROTTLE,
  STARTUP_STAGES,
  type GpuColumn,
} from "./pod-metrics";

/** The parts of a pod card's "on the pod" section (the pod's own GET /metrics). */

const unit = (value: number | null | undefined, suffix: string) =>
  value === null || value === undefined ? DASH : `${formatCount(value)} ${suffix}`;
const pct = (value: number | null | undefined) => unit(value, "%");

/** at most this many pod errors before "show all" */
const ERRORS_SHOWN = 3;

/** GPU now (tiles) with the last 15 min as a sparkline scaled to the card's capacity, and throttle reasons. */
export function PodGauges({ metrics, color }: { metrics: PodMetrics; color: string }) {
  const { t } = useLocalization();
  const { gpu } = metrics;
  const now = gpu.now;
  const w5 = gpu.windows?.["5m"];
  const total = gpu.memory_total_mb ?? null;
  const limit = gpu.power_limit_w ?? null;
  const memoryTone: Tone =
    w5?.memory_used_max_mb && total && w5.memory_used_max_mb / total >= LIMITS.podGpuMemoryHigh ? "warn" : "neutral";
  const hotTone: Tone = (w5?.temperature_max_c ?? 0) >= LIMITS.podGpuHotC ? "warn" : "neutral";
  const throttle = now?.throttle ?? [];
  const slowed = throttle.some((reason) => SLOWING_THROTTLE.has(reason));
  // zero-based, up to the card's capacity: the line's height reads as "how close to the limit"
  const spark = (column: GpuColumn, max: number | null) => (
    <Sparkline values={gpuHistory(metrics, column)} color={color} max={max ?? undefined} height={20} className="mt-1.5" />
  );

  return (
    <div className="flex flex-col gap-2">
      {/* two pairs: 2 × 2 in a card, one row of 4 when the card is wide (never 3 + 1) */}
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 17rem), 1fr))" }}>
        <div className="grid grid-cols-2 gap-2">
          <StatTile
            size="sm"
            label={t("monitoring.pods.side.util")}
            hint={t("monitoring.pods.side.sparkHint")}
            value={pct(now?.util_pct)}
            sub={w5 ? t("monitoring.pods.side.utilSub", { avg: pct(w5.util_avg_pct), max: pct(w5.util_max_pct) }) : undefined}
          >
            {spark("util_pct", 100)}
          </StatTile>
          <StatTile
            size="sm"
            label={t("monitoring.pods.side.memory")}
            hint={t("monitoring.pods.side.sparkHint")}
            tone={memoryTone}
            value={formatMb(now?.memory_used_mb ?? null)}
            sub={t("monitoring.pods.side.memorySub", { total: formatMb(total), max: formatMb(w5?.memory_used_max_mb ?? null) })}
          >
            {spark("memory_used_mb", total)}
          </StatTile>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatTile
            size="sm"
            label={t("monitoring.pods.side.temperature")}
            hint={t("monitoring.pods.side.sparkHint")}
            tone={hotTone}
            value={unit(now?.temperature_c, "°C")}
            sub={w5 ? t("monitoring.pods.side.temperatureSub", { max: unit(w5.temperature_max_c, "°C") }) : undefined}
          >
            {spark("temperature_c", 100)}
          </StatTile>
          <StatTile
            size="sm"
            label={t("monitoring.pods.side.power")}
            hint={t("monitoring.pods.side.sparkHint")}
            value={unit(now?.power_w, "W")}
            sub={t("monitoring.pods.side.powerSub", { limit: unit(limit, "W"), avg: unit(w5?.power_avg_w, "W") })}
          >
            {spark("power_w", limit)}
          </StatTile>
        </div>
      </div>
      {throttle.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          {slowed && <SeverityIcon severity="warning" className="h-3.5 w-3.5" />}
          <span className={slowed ? "text-amber-300" : "text-muted-foreground"}>{t("monitoring.pods.side.throttle")}</span>
          {throttle.map((reason) => (
            <span
              key={reason}
              title={
                SLOWING_THROTTLE.has(reason) ? t("monitoring.pods.side.throttleSlowHint") : t("monitoring.pods.side.throttleCapHint")
              }
              className={cn(
                "inline-flex h-5 items-center rounded-md border px-1.5 font-mono text-[10px]",
                SLOWING_THROTTLE.has(reason) ? "border-amber-500/35 text-amber-300" : "border-border text-muted-foreground",
              )}
            >
              {reason}
            </span>
          ))}
          {now?.sm_clock_mhz ? (
            <span className="tabular-nums text-muted-foreground">
              · {t("monitoring.pods.side.smClock")} {unit(now.sm_clock_mhz, "MHz")}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The nets on the pod and where they run: a CPU fallback is 10-50x slower, so it is an error. */
export function PodModels({ models }: { models: PodModel[] }) {
  const { t } = useLocalization();
  if (!models.length) return null;
  return (
    <div className="flex flex-col gap-2">
      <SubHeading>{t("monitoring.pods.side.models")}</SubHeading>
      <ul className="flex flex-wrap gap-1.5">
        {models.map((model) => {
          const cpu = runsOnCpu(model);
          const where = !model.loaded
            ? t("monitoring.pods.side.notLoaded")
            : runsOnGpu(model)
              ? t("monitoring.pods.side.onGpu")
              : t("monitoring.pods.side.onCpu");
          const title = [cpu ? t("monitoring.pods.side.cpuHint") : null, model.file, model.providers.join(", ") || null]
            .filter(Boolean)
            .join(" · ");
          return (
            <li
              key={model.key}
              title={title}
              className={cn(
                "inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-xs",
                cpu
                  ? "border-red-500/40 bg-red-500/15 text-red-300"
                  : model.loaded
                    ? "border-border/70"
                    : "border-dashed text-muted-foreground",
              )}
            >
              {cpu && <SeverityIcon severity="critical" className="h-3.5 w-3.5" />}
              <span>{translateOptional(`monitoring.pods.side.model.${model.key}`) ?? model.key}</span>
              <span className={cn("font-mono text-[10px]", !cpu && "text-muted-foreground")}>{where}</span>
              {model.loaded && model.gpu_decode === true && (
                <span className="font-mono text-[10px] text-muted-foreground" title={t("monitoring.pods.side.nvjpegHint")}>
                  · nvJPEG
                </span>
              )}
              {model.loaded && model.gpu_decode === false && (
                <span className="text-[10px] text-muted-foreground" title={t("monitoring.pods.side.cpuDecodeHint")}>
                  · {t("monitoring.pods.side.cpuDecode")}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Pod-side latency of the inference routes over 5 min (no network in it); the rest as a muted line. */
export function PodRoutes({ metrics }: { metrics: PodMetrics }) {
  const { t } = useLocalization();
  const routes = metrics.routes ?? {};
  const inference = INFERENCE_ROUTES.filter((path) => routes[path]);
  const others = Object.entries(routes)
    .filter(([path]) => !(INFERENCE_ROUTES as readonly string[]).includes(path))
    .map(([path, route]) => ({ path, count: route.windows?.["5m"]?.count ?? 0 }))
    .filter((route) => route.count > 0)
    // unknown paths (scanners) last
    .sort((a, b) => Number(a.path === OTHER_ROUTE) - Number(b.path === OTHER_ROUTE) || a.path.localeCompare(b.path));

  return (
    <div className="flex flex-col gap-2">
      <SubHeading>
        <span title={t("monitoring.pods.side.routesHint")}>{t("monitoring.pods.side.routes")}</span>
      </SubHeading>
      {inference.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("monitoring.pods.side.noInference")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="text-[11px] text-muted-foreground">
                <th className="py-1 pr-2 text-left font-normal">{t("monitoring.requests.col.route")}</th>
                <th className="px-1.5 py-1 text-right font-normal">{t("monitoring.pods.side.col.count")}</th>
                <th className="px-1.5 py-1 text-right font-normal">{t("monitoring.series.p50")}</th>
                <th className="px-1.5 py-1 text-right font-normal">p90</th>
                <th className="py-1 pl-1.5 text-right font-normal">{t("monitoring.pods.side.col.errors")}</th>
              </tr>
            </thead>
            <tbody>
              {inference.map((path) => {
                const w5 = routes[path].windows?.["5m"];
                const errors = w5?.errors ?? 0;
                const clientErrors = w5?.client_errors ?? 0;
                return (
                  <tr key={path} className={cn("border-t border-border/50", !w5?.count && "text-muted-foreground")}>
                    <td className="whitespace-nowrap py-1.5 pr-2 font-mono">{path}</td>
                    <td className="px-1.5 py-1.5 text-right font-mono">{formatCount(w5?.count ?? 0)}</td>
                    <td className="whitespace-nowrap px-1.5 py-1.5 text-right font-mono">{formatMs(w5?.p50_ms)}</td>
                    <td className="whitespace-nowrap px-1.5 py-1.5 text-right font-mono">{formatMs(w5?.p90_ms)}</td>
                    {/* the 4xx part drops below the 5xx count when the card is narrow */}
                    <td className="py-1.5 pl-1.5 text-right font-mono">
                      <span className={cn(errors > 0 && toneText("warn"))}>{formatCount(errors)}</span>
                      {clientErrors > 0 && (
                        <span
                          className="ml-1 inline-block whitespace-nowrap text-muted-foreground"
                          title={t("monitoring.pods.side.clientErrorsHint")}
                        >
                          +{formatCount(clientErrors)} 4xx
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {others.length > 0 && (
        <p className="break-words text-[11px] text-muted-foreground">
          {t("monitoring.pods.side.otherRoutes")}{" "}
          {others.map((route, i) => (
            <Fragment key={route.path}>
              {i > 0 && " · "}
              <span title={route.path === OTHER_ROUTE ? t("monitoring.pods.side.otherHint") : undefined}>
                {route.path === OTHER_ROUTE ? t("monitoring.pods.side.other") : <span className="font-mono">{route.path}</span>}{" "}
                <span className="tabular-nums">{formatCount(route.count)}</span>
              </span>
            </Fragment>
          ))}
        </p>
      )}
    </div>
  );
}

/** Where the pod's time goes: each stage's 5 min p50 against its route's p50, then the startup timers. */
export function PodStages({ metrics }: { metrics: PodMetrics }) {
  const { t } = useLocalization();
  const stages = metrics.stages ?? {};
  const rows = INFERENCE_ROUTES.map((path) => ({
    path,
    total: metrics.routes?.[path]?.windows?.["5m"]?.p50_ms ?? null,
    stages: Object.entries(stages[path] ?? {})
      .map(([name, stage]) => ({ name, p50: stage.windows?.["5m"]?.p50_ms ?? null }))
      .filter((stage): stage is { name: string; p50: number } => stage.p50 !== null)
      .sort((a, b) => b.p50 - a.p50),
  })).filter((row) => row.stages.length);
  const startup = Object.entries(stages[STARTUP_STAGES] ?? {})
    .filter(([, stage]) => stage.last_ms !== null)
    .map(([name, stage]) => ({ label: <span className="font-mono">{name}</span>, value: formatMs(stage.last_ms) }));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <SubHeading>{t("monitoring.pods.side.stages")}</SubHeading>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("monitoring.pods.side.stagesEmpty")}</p>
        ) : (
          <>
            {rows.map((row) => (
              <div key={row.path} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-[11px]">
                  <span className="font-mono">{row.path}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {t("monitoring.pods.side.stagesTotal", { p50: formatMs(row.total) })}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(0,max-content)_minmax(2.5rem,1fr)_auto] items-center gap-x-2 gap-y-1 text-[11px]">
                  {row.stages.map((stage) => (
                    <Fragment key={stage.name}>
                      <span className="min-w-0 font-mono text-muted-foreground [overflow-wrap:anywhere]">{stage.name}</span>
                      <Meter value={row.total ? stage.p50 / row.total : null} className="h-1" />
                      <span className="text-right font-mono tabular-nums">{formatMs(stage.p50)}</span>
                    </Fragment>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">{t("monitoring.pods.side.stagesNote")}</p>
          </>
        )}
      </div>
      {startup.length > 0 && (
        <div className="flex flex-col gap-1">
          <SubHeading>{t("monitoring.pods.side.startup")}</SubHeading>
          <FactList items={startup} min="13rem" />
        </div>
      )}
    </div>
  );
}

/** The pod's event loop, process, host and identity. */
export function PodProcess({ metrics }: { metrics: PodMetrics }) {
  const { t } = useLocalization();
  const { service, gpu, host } = metrics;
  const loop5 = metrics.loop?.windows?.["5m"];
  const hostNow = host?.now;
  const host5 = host?.windows?.["5m"];
  const lagMax = loopMax5m(metrics);

  return (
    <div className="flex flex-col gap-1">
      <SubHeading>{t("monitoring.pods.side.process")}</SubHeading>
      <FactList
        min="17rem"
        items={[
          {
            label: t("monitoring.pods.side.loop"),
            hint: t("monitoring.pods.side.loopHint"),
            tone: lagMax !== null && lagMax >= LIMITS.podLoopLagMs ? "warn" : undefined,
            value: loop5 ? t("monitoring.pods.side.loopValue", { max: formatMs(loop5.max_ms), p99: formatMs(loop5.p99_ms) }) : DASH,
          },
          {
            label: t("monitoring.pods.side.over100"),
            hint: t("monitoring.pods.side.over100Hint"),
            value: formatCount(loop5?.over_100ms ?? null),
          },
          {
            label: t("monitoring.pods.side.processCpu"),
            hint: t("monitoring.backend.res.cpuSub"),
            value: t("monitoring.pods.side.nowMax", { now: pct(hostNow?.process_cpu_pct), max: pct(host5?.process_cpu_max_pct) }),
          },
          {
            label: t("monitoring.pods.side.rss"),
            value: t("monitoring.pods.side.nowMax", {
              now: formatMb(hostNow?.process_rss_mb ?? null),
              max: formatMb(host5?.rss_max_mb ?? null),
            }),
          },
          { label: t("monitoring.backend.res.threads"), value: formatCount(hostNow?.process_threads ?? null) },
          {
            label: t("monitoring.pods.side.hostCpu"),
            value: t("monitoring.pods.side.hostCpuValue", {
              pct: pct(hostNow?.cpu_pct),
              cores: formatCount(hostNow?.cpu_count ?? null),
              load: hostNow?.load_avg?.map((v) => formatCount(v)).join(" / ") ?? DASH,
            }),
          },
          {
            label: t("monitoring.pods.side.hostMemory"),
            value: `${formatMb(hostNow?.memory_used_mb ?? null)} / ${formatMb(hostNow?.memory_total_mb ?? null)}`,
          },
          {
            label: t("monitoring.pods.side.pcieNow"),
            hint: t("monitoring.pods.side.pcieNowHint"),
            value: gpu.now?.pcie_gen || gpu.now?.pcie_width ? pcieLink(gpu.now.pcie_gen, gpu.now.pcie_width) : DASH,
          },
          {
            label: t("monitoring.pods.side.driver"),
            value: gpu.driver ? `${gpu.driver}${gpu.cuda_driver ? ` · CUDA ${gpu.cuda_driver}` : ""}` : DASH,
          },
          { label: t("monitoring.backend.process.started"), value: formatStamp(service.started_at) },
          { label: t("monitoring.pods.side.uptimeLabel"), value: formatDuration(service.uptime_s) },
          { label: t("monitoring.pods.side.podId"), value: service.pod_id ?? DASH },
          { label: t("monitoring.backend.process.hostname"), value: service.hostname || DASH },
          { label: t("monitoring.backend.process.pid"), value: formatCount(service.pid) },
        ]}
      />
    </div>
  );
}

/** The pod's last errors (newest first): three, the rest on demand. */
export function PodErrors({ errors, now }: { errors: PodError[]; now: number }) {
  const { t } = useLocalization();
  const [all, setAll] = useState(false);
  if (!errors.length) return null;
  const shown = all ? errors : errors.slice(0, ERRORS_SHOWN);

  return (
    <div className="flex flex-col gap-2">
      <SubHeading
        actions={
          errors.length > ERRORS_SHOWN ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => setAll(!all)}
              aria-expanded={all}
            >
              {all ? t("monitoring.pods.side.errorsFewer") : t("monitoring.pods.side.errorsAll", { count: errors.length })}
            </Button>
          ) : undefined
        }
      >
        {t("monitoring.pods.side.errors")}
      </SubHeading>
      <ul className={cn("flex flex-col gap-1.5", all && "max-h-96 overflow-y-auto pr-1")}>
        {shown.map((error, i) => {
          const current = isRecent(error.at, now);
          const server = error.status === null || error.status >= 500;
          return (
            <li
              key={`${error.at}-${i}`}
              className={cn(
                "rounded-md border px-2.5 py-1.5 text-xs",
                !current
                  ? "border-border/70 text-muted-foreground"
                  : server
                    ? "border-red-500/30 bg-red-500/10 text-red-300"
                    : "border-amber-500/35 text-amber-300",
              )}
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-mono">{error.route}</span>
                <span className="font-mono">{error.status ?? DASH}</span>
                <Ago ts={error.at} className="text-[11px] opacity-80" />
              </div>
              <p className="mt-0.5 break-words font-mono text-[11px] leading-snug [overflow-wrap:anywhere]">
                {error.error ?? t("monitoring.pods.side.noMessage")}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
